import { JSX } from "react";
import { Page, View, Document } from "@react-pdf/renderer";
import { styles, spacing } from "components/Resume/ResumePDF/styles";
import { FontSizeProvider } from "components/Resume/ResumePDF/FontSizeContext";
import { ResumePDFProfile } from "components/Resume/ResumePDF/ResumePDFProfile";
import { Profile } from "./Profile";
import { ResumePDFWorkExperience } from "components/Resume/ResumePDF/ResumePDFWorkExperience";
import { ResumePDFEducation } from "components/Resume/ResumePDF/ResumePDFEducation";
import { ResumePDFProject } from "components/Resume/ResumePDF/ResumePDFProject";
import { ResumePDFSkills } from "components/Resume/ResumePDF/ResumePDFSkills";
import { ResumePDFCustom } from "components/Resume/ResumePDF/ResumePDFCustom";
import { DEFAULT_FONT_COLOR } from "lib/redux/settingsSlice";
import type { Settings, ShowForm } from "lib/redux/settingsSlice";
import type { Resume } from "lib/redux/types";
import { SuppressResumePDFErrorMessage } from "components/Resume/ResumePDF/common/SuppressResumePDFErrorMessage";
import { Summary } from "./Summary";
import { Education } from "./Education";
import { Project } from "./Project";
import { WorkExperience } from "./WorkExperience";
import { Skills } from "./Skills";
import { Award } from "./Award";
import { Custom } from "./Custom";
import { Certificate } from "./Certificate";
import { Language } from "./Language";
import { A4_HEIGHT_PT, LETTER_HEIGHT_PT } from "lib/constants";

export const TemplateFive = ({
  resume,
  settings,
  isPDF = false,
  enableHighlight = false,
  enableMistakeHighlight = false,
}: {
  resume: Resume;
  settings: Settings;
  isPDF?: boolean;
  enableHighlight?: boolean;
  enableMistakeHighlight?: boolean;
}) => {
  const {
    profile,
    workExperiences,
    educations,
    projects,
    skills,
    certifications,
    languages,
    awards,
    custom,
  } = resume;
  const { name } = profile;
  const {
    fontFamily,
    fontSize,
    documentSize,
    formToHeading,
    formToShow,
    formsOrder,
  } = settings;
  const themeColor = settings.themeColor || DEFAULT_FONT_COLOR;

  const showFormsOrder = formsOrder.filter((form) => formToShow[form]);

  const formTypeToComponent: { [type in ShowForm]: () => JSX.Element } = {
    workExperiences: () => (
      <WorkExperience
        heading={formToHeading["workExperiences"]}
        workExperiences={workExperiences}
        themeColor={themeColor}
        enableHighlight={enableHighlight}
        enableMistakeHighlight={enableMistakeHighlight}
      />
    ),
    educations: () => (
      <Education
        heading={formToHeading["educations"]}
        educations={educations}
        themeColor={themeColor}
        enableHighlight={enableHighlight}
        enableMistakeHighlight={enableMistakeHighlight}
      />
    ),
    projects: () => (
      <Project
        heading={formToHeading["projects"]}
        projects={projects}
        themeColor={themeColor}
        isPDF={isPDF}
        enableHighlight={enableHighlight}
        enableMistakeHighlight={enableMistakeHighlight}
      />
    ),
    skills: () => (
      <Skills
        heading={formToHeading["skills"]}
        skills={skills}
        themeColor={themeColor}
      />
    ),
    certifications: () => (
      <Certificate
        heading={formToHeading["certifications"]}
        certifications={certifications}
        themeColor={themeColor}
        isPDF={isPDF}
        enableMistakeHighlight={enableMistakeHighlight}
      />
    ),
    languages: () => (
      <Language
        heading={formToHeading["languages"]}
        languages={languages}
        themeColor={themeColor}
        enableMistakeHighlight={enableMistakeHighlight}
      />
    ),
    awards: () => (
      <Award
        heading={formToHeading["awards"]}
        awards={awards}
        themeColor={themeColor}
        enableMistakeHighlight={enableMistakeHighlight}
      />
    ),
    custom: () => (
      <Custom
        heading={formToHeading["custom"]}
        custom={custom}
        themeColor={themeColor}
        enableMistakeHighlight={enableMistakeHighlight}
      />
    ),
  };

  return (
    <>
      <Document title={`${name} Resume`} author={name} producer={"OpenResume"}>
        <Page
          size={documentSize === "A4" ? "A4" : "LETTER"}
          style={{
            ...styles.flexCol,
            color: DEFAULT_FONT_COLOR,
            fontFamily,
            fontSize: fontSize + "pt",
            height:
              documentSize === "A4"
                ? `${A4_HEIGHT_PT}pt`
                : `${LETTER_HEIGHT_PT}pt`,
          }}
        >
          <FontSizeProvider fontSize={fontSize}>
            <View
              style={{
                height: "15pt",
                width: "20%",
                backgroundColor: themeColor,
                paddingLeft: spacing[10],
                marginBottom: "-0.75pt",
              }}
              fixed={true}
            ></View>
            <View
              style={{
                ...styles.flexCol,
                marginTop: "-25pt", // -${spacing["5"]} (-60pt) to -25pt
              }}
            >
              <Profile profile={profile} themeColor={themeColor} isPDF={isPDF} enableHighlight={enableHighlight} />
              {profile.summary && (
                <Summary
                  heading="SUMMARY"
                  summary={profile.summary}
                  themeColor={themeColor}
                  enableHighlight={enableHighlight}
                  enableMistakeHighlight={enableMistakeHighlight}
                />
              )}
              {showFormsOrder.map((form) => {
                const Component = formTypeToComponent[form];
                return <Component key={form} />;
              })}
            </View>
            <View
              style={{
                width: "100%",
                flex: 1,
                ...styles.flexRow,
                marginTop: "-6pt", // -${spacing[1]} (-12pt) to -6pt
              }}
            >
              <View
                style={{
                  width: "20%",
                  backgroundColor: themeColor,
                  paddingLeft: spacing[10],
                }}
              />
              <View style={{ width: "80%", paddingLeft: spacing[5] }} />
            </View>
          </FontSizeProvider>
        </Page>
      </Document>
      <SuppressResumePDFErrorMessage />
    </>
  );
};
